import { Button, DialogActions, Grid, Skeleton, TextField, Typography } from '@mui/material';
import {
  CalculateFeaturesJob,
  getSecondaryAnalysisJobPrimaryRunOrchestrationId,
  isSecondaryAnalysisJob,
} from 'interfaces/job';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';

import { DialogWithCloseButton } from 'components/atoms/Dialog/DialogWithCloseButton';
import { useRunSecondaryAnalysisRebuild } from 'services/secondaryAnalysis/useRunSecondaryAnalysisRebuild';
import { useJob } from 'utils/queryHooks/jobs/useJob';
import { CasesParams } from 'utils/useCasesParams/types';

interface RebuildSecondaryAnalysisActionProps {
  onClose: (event?: any, force?: boolean) => void;
  casesParams: CasesParams;
  jobId?: string;
  platformVersionWarning?: React.ReactNode;
}

export const RebuildSecondaryAnalysisAction: React.FC<React.PropsWithChildren<RebuildSecondaryAnalysisActionProps>> = ({
  onClose,
  casesParams,
  jobId,
  platformVersionWarning,
}) => {
  const [jobName, setJobName] = useState('');
  const [jobDescription, setJobDescription] = useState('');

  const {
    data: sourceJob,
    isLoading: isSourceJobLoading,
    isFetched: isSourceJobFetched,
  } = useJob<CalculateFeaturesJob>(jobId, {
    enabled: !isEmpty(jobId),
    retry: false,
  });

  useEffect(() => {
    if (!sourceJob) {
      return;
    }

    setJobName(sourceJob.name || 'Secondary Analysis Rebuild');
    setJobDescription(sourceJob.description || '');
  }, [sourceJob]);

  const { runSecondaryAnalysisRebuild, isStartingSecondaryAnalysisRebuild } = useRunSecondaryAnalysisRebuild({
    onSuccess: () => onClose(undefined, true),
  });

  const hasValidSecondaryAnalysisSource = isSecondaryAnalysisJob(sourceJob);

  const submitDisabled =
    isStartingSecondaryAnalysisRebuild || isSourceJobLoading || !sourceJob || !hasValidSecondaryAnalysisSource;

  return (
    <DialogWithCloseButton header="Rebuild Secondary Analysis" onClose={onClose}>
      {platformVersionWarning}
      {isSourceJobLoading ? (
        <Skeleton variant="rectangular" width="100%" height={180} />
      ) : (
        <Grid container direction="column" spacing={2}>
          {!hasValidSecondaryAnalysisSource && isSourceJobFetched && (
            <Grid item>
              <Typography color="error">This job is not a valid secondary analysis rebuild source.</Typography>
            </Grid>
          )}
          <Grid item>
            <TextField
              fullWidth
              name="jobName"
              label="Job Name"
              value={jobName}
              onChange={(event) => setJobName(event.target.value)}
              placeholder="Type Here"
            />
          </Grid>
          <Grid item>
            <TextField
              fullWidth
              name="jobDescription"
              label="Job Description"
              value={jobDescription}
              onChange={(event) => setJobDescription(event.target.value)}
              placeholder="Type Here"
              multiline
              minRows={4}
            />
          </Grid>
        </Grid>
      )}
      <DialogActions sx={{ marginTop: 2 }}>
        <Button onClick={() => onClose(undefined, true)} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={() =>
            runSecondaryAnalysisRebuild({
              sourceJob: {
                studyId: sourceJob?.studyId || casesParams?.studyId,
                primaryRunOrchestrationId: getSecondaryAnalysisJobPrimaryRunOrchestrationId(sourceJob),
                params: sourceJob?.params,
                manifest: sourceJob?.manifest,
              },
              jobName,
              jobDescription,
            })
          }
          disabled={submitDisabled}
          variant="contained"
        >
          {isStartingSecondaryAnalysisRebuild ? 'Starting Job...' : 'Rebuild'}
        </Button>
      </DialogActions>
    </DialogWithCloseButton>
  );
};
